/**
 * Pure RBAC helper — what roles is the actor allowed to see/assign in
 * the role select? Shared by:
 *   - <EmployeesAddModal>     — invite flow (role select on step 1)
 *   - <EmployeesDetailModal>  — edit flow (role select in detail card)
 *
 * Driven by `ROLE_ACCESS_LEVEL` from `@aris/shared` so the FE doesn't
 * keep its own tier list (V2-FIX-17 / F-13).
 *
 * ⚠️ THE TWO CALL SITES HAVE DIFFERENT SERVER CONTRACTS — hence `surface`.
 *   The two modals post to DIFFERENT endpoints, and for a SUPERVISOR actor
 *   those endpoints disagree about which tiers are allowed:
 *     - invite → `POST /invites` → `InvitesService.assertCanInvite`:
 *       SUPERVISOR is tier-EQUALITY, ADMIN-tier ONLY; a MANAGER/EMPLOYEE
 *       target is `throw this.forbidden()`.
 *     - edit   → `PATCH /users/:id` → `UsersService.update`: SUPERVISOR may
 *       assign ANY tier below SUPERVISOR (the `admin_cannot_*` gates are
 *       ADMIN-actor-only; the rank check passes MANAGER/EMPLOYEE).
 *   A single clamp cannot mirror both. `surface` is REQUIRED (no default)
 *   so a new call site must state which contract it answers to rather than
 *   silently inheriting the wrong one.
 *
 * RTF L79 — devil-advocate post-deploy bar 1:
 *   Admin handles ONLY Admin/РО tiers. Сотрудник-tier (EMPLOYEE) belongs
 *   to РО. The detail modal previously showed Admin the unfiltered role
 *   list AND the BE only enforced the rank hierarchy — so an Admin could
 *   demote any user to EMPLOYEE-tier in one click. The BE now rejects
 *   that with `admin_cannot_assign_employee_role`; this helper is the
 *   FE-side mirror that hides the option upfront.
 *
 * GLOBAL admin-appointment rule (assistant-parity, 2026-07):
 *   Only a SUPERVISOR may appoint an ADMIN-tier role. This is org-wide —
 *   it applies to EVERY non-supervisor actor (generic ADMIN, ГИП,
 *   ASSISTANT_GIP), mirroring the BE `ONLY_SUPERVISOR_ASSIGNS_ADMIN` gate
 *   (invites.service / users.service). So the ADMIN branch below clamps to
 *   MANAGER-tier assignable roles only (РО/DEPT_HEAD) — never
 *   GIP/ASSISTANT_GIP/TECH_DIRECTOR/BIM_MANAGER — so every rendered option
 *   is submittable. The ADMIN branch IS surface-invariant: its invite and
 *   edit contracts agree on MANAGER-tier-only (`assertCanInvite` 403s an
 *   EMPLOYEE-tier and a SUPERVISOR-tier invite, and the GLOBAL rule mirror
 *   403s an ADMIN-tier one; on edit, `admin_cannot_assign_employee_role`
 *   blocks downward and the P3 gate blocks upward).
 *
 * ⚠️ ПОПРАВКА 03.09.2026 (круг ревью Р-114, находка Codex) — ПРЕЖНЯЯ
 *   РЕДАКЦИЯ ЗДЕСЬ ГОВОРИЛА «The ADMIN **and MANAGER** branches are
 *   surface-INVARIANT: their invite and edit contracts already agree», И
 *   ПРО MANAGER ЭТО НЕВЕРНО. Договоры у него не совпадают, а РАСХОДЯТСЯ, и
 *   расходятся сильнее, чем у супервайзера:
 *     - invite → `assertCanInvite`: РО МОЖЕТ приглашать любую роль
 *       EMPLOYEE-яруса в СВОЙ отдел (`invites.service.ts` — ветка MANAGER:
 *       `targetLevel !== EMPLOYEE` → 403, `input.departmentId !==
 *       actor.departmentId` → 403). Список ниже для этой поверхности верен.
 *     - edit → `UsersService.update`: `roleId` ОТСУТСТВУЕТ в `allowedKeys`
 *       для MANAGER вовсе (`['email','phone','fullName','hireDate']`), то
 *       есть ярус здесь ни при чём — роль ему не менять НИКАКУЮ.
 *   Совпадением это выглядело потому, что на поверхности правки список
 *   инертен (матрица `fieldMatrixFor` держит `role: false` литералом, селект
 *   отрисован выключенным) — но «не видно» и «согласовано» это разные вещи.
 *   Предмет и цена сняты долгом #273; сама ветка ниже (`:168-174`) описывала
 *   расхождение верно с самого начала — неверен был этот абзац.
 *
 * supervisor-invites-admin (2026-06-10), INVITE surface: SUPERVISOR may
 *   assign ADMIN-tier roles ONLY (GIP / ASSISTANT_GIP / TECH_DIRECTOR /
 *   BIM_MANAGER) — tier EQUALITY, mirroring InvitesService.assertCanInvite.
 *   MANAGER / EMPLOYEE / SUPERVISOR-tier roles stay hidden.
 *
 * Р-114 (2026-09-03), EDIT surface: SUPERVISOR sees EVERY assignable role
 *   BELOW supervisor tier — ADMIN + MANAGER + EMPLOYEE.
 *
 *   WHY the supervisor gets the whole spectrum here and nobody else does:
 *   the supervisor is the ONLY actor the server lets both (a) touch a
 *   rank-and-file EMPLOYEE-tier card at all — `admin_cannot_modify_employee_tier`
 *   blocks the ADMIN actor outright — and (b) assign roles of every tier —
 *   `admin_cannot_assign_employee_role` blocks ADMIN downward, the P3 gate
 *   blocks ADMIN upward, and a MANAGER cannot change `roleId` at all (it is
 *   absent from their `allowedKeys` edit-set). The old tier-EQUALITY clamp
 *   was copied here from the invite surface, and on the EDIT surface it
 *   produced a DEAD END: «повысить рядового сотрудника в руководители
 *   отдела не может НИКТО». The supervisor was allowed by every server
 *   check but the option was never rendered to him.
 *
 * ⚠️ ПОПРАВКА 03.09.2026 (круг ревью Р-114, находка Codex) — ПРО ГИПа ЗДЕСЬ
 *   БЫЛО НАПИСАНО «saw «Руководитель отдела» in the list but got 403 on a
 *   rank-and-file target», И ЭТО НЕВЕРНО В ЧАСТИ «got 403»: до отказа он не
 *   доходит, потому что до СЕЛЕКТА не доходит. Актор ADMIN-яруса на карточке
 *   рядового получает `NO_EDIT` целиком (`employees-detail.modal.tsx:198-200`,
 *   ветка `target.accessLevel === 'EMPLOYEE'`), поэтому кнопки
 *   «Редактировать» на такой карточке нет вовсе — форма не открывается, роль
 *   не выбирается, 403 не наступает. Серверный рубеж
 *   `admin_cannot_modify_employee_tier` при этом на месте и верен — он
 *   ВТОРАЯ линия, а первая (матрица) отсекает раньше. Разница не
 *   косметическая: «выбрал и получил отказ» описывает достижимый путь,
 *   которого нет, и следующий читатель пошёл бы искать сообщение об ошибке
 *   там, где нет даже формы. Тупик от этого не становится меньше — он ровно
 *   тот же: рядового в руководители отдела не повышал НИКТО.
 *
 *   Live case (customer, PROD, 03.09.2026): a department head was
 *   dismissed and there was no way to appoint a successor through the UI.
 *   The same dead end existed on the way DOWN — demoting a РО back to
 *   rank-and-file is likewise supervisor-only server-side.
 *
 *   The server stays the second line: it keeps every check of its own, and
 *   this helper is only the mirror that stops offering unsubmittable options.
 */
import { ROLE_ACCESS_LEVEL, type AccessLevel } from '@aris/shared';

import type { RoleRow } from '@/api/roles';

/**
 * Which modal is asking. See the `surface` note in the module header: the
 * invite and edit endpoints enforce DIFFERENT tier rules for a SUPERVISOR,
 * so the caller must say which server contract its submit will hit.
 */
export type RoleVisibilitySurface = 'invite' | 'edit';

/**
 * Shared copy — shown to a NON-supervisor actor beneath the role select
 * (add + detail modals) to explain why no ADMIN-tier option is offered.
 * The GLOBAL rule reserves ADMIN-tier appointment to the SUPERVISOR, so
 * this single source keeps the wording consistent across both surfaces.
 */
export const ADMIN_TIER_SUPERVISOR_ONLY_HINT =
  'Роли уровня «Администратор» назначает только супервайзер.';

export interface RoleVisibilityActor {
  accessLevel: AccessLevel;
  /** Department is not used by the helper today but kept for symmetry
   *  with `AddActor` / `DetailActor` so callers can pass either. */
  departmentId?: number | null;
}

/**
 * Tiers a SUPERVISOR may assign on the EDIT surface (Р-114).
 *
 * Enumerated POSITIVELY on purpose — `!== 'SUPERVISOR'` would be the
 * shorter spelling and the wrong one: `ROLE_ACCESS_LEVEL[slug]` yields
 * `undefined` for a slug the FE constant does not know yet, and a negative
 * filter ADMITS that `undefined` (fail-OPEN). Every other branch here is
 * an equality test and therefore drops an unmapped slug; this set keeps
 * the same fail-CLOSED posture. «Инженер» (ENGINEER) regressed exactly on
 * an unmapped slug once (Block 2 Этап 01a) — in the safe direction. The
 * unsafe direction would leak CEO: it is SUPERVISOR-tier and, unlike
 * SYSADMIN, it is `isAssignable = true` in the seed (`prisma/seed.ts` —
 * only SYSADMIN is flipped to false), so the `isAssignable` filter alone
 * does NOT hold it back. The tier test is what keeps CEO out of the list.
 */
const SUPERVISOR_EDIT_TIERS: ReadonlySet<AccessLevel> = new Set<AccessLevel>([
  'ADMIN',
  'MANAGER',
  'EMPLOYEE',
]);

export function visibleAssignableRoles(
  actor: RoleVisibilityActor,
  all: RoleRow[],
  surface: RoleVisibilitySurface,
): RoleRow[] {
  const assignable = all.filter((r) => r.isAssignable);
  if (actor.accessLevel === 'ADMIN') {
    // GLOBAL rule: only a SUPERVISOR appoints an ADMIN-tier role. Every
    // non-supervisor ADMIN-tier actor (generic ADMIN, ГИП, ASSISTANT_GIP)
    // is clamped to MANAGER-tier assignable roles (РО/DEPT_HEAD) so the
    // select never lists a GIP/ASSISTANT_GIP/TECH_DIRECTOR/BIM_MANAGER
    // option that would 403 `ONLY_SUPERVISOR_ASSIGNS_ADMIN` on submit.
    // Surface-invariant: invite and edit agree for this actor.
    return assignable.filter((r) => ROLE_ACCESS_LEVEL[r.slug] === 'MANAGER');
  }
  if (actor.accessLevel === 'MANAGER') {
    // Correct for the INVITE surface (assertCanInvite lets a РО invite any
    // EMPLOYEE-tier role into their own dept). On the EDIT surface the list
    // is inert: `fieldMatrixFor` hardcodes `role: false` for a MANAGER, so
    // the select is rendered disabled and never opens — see долг #273.
    return assignable.filter((r) => ROLE_ACCESS_LEVEL[r.slug] === 'EMPLOYEE');
  }
  if (actor.accessLevel === 'SUPERVISOR') {
    if (surface === 'invite') {
      // supervisor-invites-admin: tier EQUALITY on `POST /invites` — the
      // supervisor is the sole actor that may invite an ADMIN-tier role,
      // and assertCanInvite forbids every other tier to him.
      return assignable.filter((r) => ROLE_ACCESS_LEVEL[r.slug] === 'ADMIN');
    }
    // Р-114 — EDIT surface: everything below supervisor tier. Closes the
    // «назначить руководителя отдела некому» dead end (see module header).
    return assignable.filter((r) => SUPERVISOR_EDIT_TIERS.has(ROLE_ACCESS_LEVEL[r.slug]));
  }
  return [];
}
